<template>
  <div class="carousel">
    <VueSlickCarousel
      ref="carousel"
      v-bind="$props"
      :infinite="shouldLoopInfinite"
      :class="{
        'overlay-arrows': overlayArrows,
        'with-dots': dots,
        'with-inside-arrows': isArrowInside
      }"
      v-on="$listeners"
    >
      <slot />
      <template #prevArrow>
        <button :aria-label="altPrevText" type="button" aria-disabled="true">
          <img :src="$options.locals.arrowLeft" :alt="altPrevText" />
        </button>
      </template>
      <template #nextArrow>
        <button :aria-label="altNextText" type="button" aria-disabled="false">
          <img :src="$options.locals.arrowRight" :alt="altNextText" />
        </button>
      </template>
      <template #customPaging="page">
        <div class="dot">{{ page }}</div>
      </template>
    </VueSlickCarousel>
  </div>
</template>

<script>
import VueSlickCarousel from 'vue-slick-carousel'
import ArrowLeft from './assets/images/arrow-left.svg'
import ArrowRight from './assets/images/arrow-right.svg'
import 'vue-slick-carousel/dist/vue-slick-carousel.css'

export default {
  name: 'Carousel',
  components: {
    VueSlickCarousel
  },
  props: {
    /**
     * Enables adaptive height for single slide horizontal carousels
     *
     * @type {boolean}
     */
    adaptiveHeight: {
      type: Boolean,
      default: true
    },
    /**
     * Alt text for previous button image
     *
     * @type {string}
     */
    altPrevText: {
      type: String,
      required: true
    },
    /**
     * Alt text for next button image
     *
     * @type {string}
     */
    altNextText: {
      type: String,
      required: true
    },
    /**
     * Enables prev/next arrows
     *
     * @type {boolean}
     */
    arrows: {
      type: Boolean,
      default: true
    },
    /**
     * Set the slider to be the navigation of other slider
     *
     * @type {object}
     */
    asNavFor: {
      type: Object,
      default: () => {}
    },
    /**
     * Enables autoplay
     *
     * @type {boolean}
     */
    autoplay: {
      type: Boolean,
      default: false
    },
    /**
     * Autoplay speed in milliseconds
     *
     * @type {number}
     */
    autoplaySpeed: {
      type: Number,
      default: 3000
    },
    /**
     * Enables centered view with partial prev/next slides. use with odd numbered slidesToShow counts
     *
     * @type {boolean}
     */
    centerMode: {
      type: Boolean,
      default: false
    },
    /**
     * Enables dot indicators
     *
     * @type {boolean}
     */
    dots: {
      type: Boolean,
      default: false
    },
    /**
     * Classes for slide indicator dots container, multiple classes separate by empty space
     * e.g. slick-dots custom-dots
     *
     * @type {string}
     */
    dotsClass: {
      type: String,
      default: 'slick-dots'
    },
    /**
     * Go to slide on click
     *
     * @type {boolean}
     */
    focusOnSelect: {
      type: Boolean,
      default: false
    },
    /**
     * Infinitely wrap around contents
     *
     * @type {boolean}
     */
    infinite: {
      type: Boolean,
      default: false
    },
    /**
     * Slide index to start on
     *
     * @type {number}
     */
    initialSlide: {
      type: Number,
      default: 0
    },
    /**
     * Load images or render components on demand or progressively
     *
     * @type {string}
     */
    lazyLoad: {
      type: String,
      default: null
    },
    /**
     * To indicate arrows position if it is overlay on the carousel
     *
     * @type {boolean}
     */
    overlayArrows: {
      type: Boolean,
      default: false
    },
    /**
     * Pauses autoplay while hovering on dots
     *
     * @type {boolean}
     */
    pauseOnDotsHover: {
      type: Boolean,
      default: true
    },
    /**
     * Pauses autoplay while hovering on track
     *
     * @type {boolean}
     */
    pauseOnHover: {
      type: Boolean,
      default: true
    },
    /**
     * Number of slides to scroll
     *
     * @type {number}
     */
    slidesToScroll: {
      type: Number,
      default: 1
    },
    /**
     * Number of slides to show
     *
     * @type {number}
     */
    slidesToShow: {
      type: Number,
      default: 1
    },
    /**
     * Animation speed in milliseconds
     *
     * @type {number}
     */
    speed: {
      type: Number,
      default: 500
    },
    /**
     * Enables swiping
     *
     * @type {boolean}
     */
    swipe: {
      type: Boolean,
      default: true
    },
    /**
     * Breakpoints and settings objects. Enables settings sets at given screen width. set settings to "unslick" instead of an object to disable slick at a given breakpoint.
     *
     * @type {Array}
     */
    responsive: {
      type: Array,
      default: () => []
    },
    /**
     * Enables vertical slide mode
     *
     * @type {boolean}
     */
    variableWidth: {
      type: Boolean,
      default: false
    },
    /**
     * Enables vertical slide mode
     *
     * @type {boolean}
     */
    vertical: {
      type: Boolean,
      default: false
    },
    isArrowInside: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      shouldLoopInfinite: false
    }
  },
  mounted() {
    // For SSR app, set :infinite to true only after hydration to prevent
    // the carousel element clones from getting displayed on server side render
    // causing flickering of duplicate elements on hydration to client side
    // Ref: https://sephora-asia.atlassian.net/browse/SHOP-1026
    if (this.infinite) {
      this.shouldLoopInfinite = true
    }
  },
  locals: {
    arrowLeft: ArrowLeft,
    arrowRight: ArrowRight
  }
}
</script>

<style lang="scss" scoped>
.carousel {
  width: 100%;
}

.slick-slider {
  display: flex !important;
  flex-wrap: wrap;
  align-items: center;

  &.overlay-arrows {
    display: block;

    &:hover {
      .slick-arrow {
        display: block;
      }
    }

    /deep/ .slick-slide {
      padding: 0;
    }
  }
}
/deep/ .slick-slide {
  .video-slide {
    position: relative;
    padding-bottom: 56.25%; /* 16:9 */

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  img {
    pointer-events: none;
  }
}

/deep/ .slick-list {
  flex: 1;
  min-width: calc(100% - 90px);

  .slick-slide {
    padding-left: 5px;
    padding-right: 5px;
  }
}

/* Arrows */
.slick-arrow {
  width: 40px;
  height: 60px;
  cursor: pointer;
  color: transparent;
  border: none;
  background: rgba($white, 0.5);
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 40px;
    height: 40px;
  }

  &.slick-prev {
    margin-right: 5px;
  }

  &.slick-next {
    margin-left: 5px;
  }

  &.slick-prev:hover,
  &.slick-prev:focus,
  &.slick-next:hover,
  &.slick-next:focus {
    opacity: 1;
  }

  &.slick-prev.slick-disabled,
  &.slick-next.slick-disabled {
    opacity: 0.3;
  }
}

.overlay-arrows {
  .slick-arrow {
    position: absolute;
    top: 50%;
    transform: translate(0, -50%);
    display: none;
    z-index: 1;
  }

  &.with-dots {
    .slick-arrow {
      top: 46%;
      transform: translate(0, -46%);
    }
  }

  .slick-prev {
    left: 0;
    margin-right: 0;
  }

  .slick-next {
    right: 0;
    margin-left: 0;
  }
}

/* Dots */
/deep/ .slick-dots {
  list-style: none;
  text-align: center;
  flex-basis: 100%;
  margin-top: 10px;
  height: 10px;

  li {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin-left: 5px;
    margin-right: 5px;

    .dot {
      width: 100%;
      height: 100%;
      padding: 0;
      cursor: pointer;
      color: transparent;
      border: 0;
      border-radius: 100%;
      background-color: $grey;
      vertical-align: top;

      &:hover,
      &:focus {
        outline: 0;
      }
    }

    &.slick-active .dot {
      background-color: $grey-darker;
    }
  }
}

.with-inside-arrows {
  position: relative;
  /deep/ .slick-list {
    min-width: 100%;

    .slick-slide {
      padding-left: 8.5px;
      padding-right: 8.5px;
    }
  }

  .slick-arrow {
    width: 30px;
    height: 30px;
    position: absolute;
    background: $white;
    border: 1px solid $grey-light;
    border-radius: 50%;
    z-index: $zindex-fixed;

    img {
      width: 15px;
      height: 15px;
    }

    &.slick-prev {
      left: 10px;
    }

    &.slick-next {
      right: 10px;
    }
  }
}
</style>
